import _ from 'lodash';
import { ServiceClient } from '@services/coralservices/ServiceClient';
import { EnvironmentDetail } from '@src/utils/EnvironmentDetailUtils';
import IhmLighthouseService from 'IhmLighthouseService-coral-javascript-clients/jquery-client';
import { AppInfoLoader, AssociateAppEvent } from '@src/services';
import { CountMetric } from '@src/metrics';
import('@amzn/ihm-coral-config/coral');

const euStores = ['UK-WFM-GAMMA', 'GB-ENG-305', 'GB-ENG-310', 'GB-ENG-309', 'GB-ENG-308', 'GB-ENG-306', 'GB-ENG-311', 'GB-ENG-307'];
let realmOverride: undefined | string = undefined;
const taskToolId = 'tmoe_tasks_tool';

if (euStores.includes(EnvironmentDetail.getStoreId()) && EnvironmentDetail.getInitialTool() === taskToolId) {
    realmOverride = 'eu-west-1';
    console.log(`Overriding Lighthouse EU stores to ${EnvironmentDetail.getDomain()}.${realmOverride}`);
}

const client = new ServiceClient('IhmLighthouseService', IhmLighthouseService, undefined, realmOverride);

export class IhmLighthouseServiceClient {
    public static sendRequest(operation: string, data: { [key: string]: any }, storeId: string) {
        const dataToSend: { [key: string]: any } = _.cloneDeep(data);
        dataToSend.storeId = storeId; // Include the storeId in the input for store isolation routing
        return client.sendRequest(
            {
                operation,
                data: dataToSend,
            },
            storeId,
        );
    }

    public static createTask(
        storeId,
        taskCategory,
        taskParams,
        taskDescription,
        requestId,
        expirationMillis,
        clientTaskId,
        taskExecutionOrderId,
        priority,
        disallowClobber,
        taskTitle,
        createdBy,
        groupingId,
    ) {
        return IhmLighthouseServiceClient.sendRequest(
            'CreateTask',
            {
                storeId,
                taskCategory,
                taskParams,
                taskDescription,
                requestId,
                expirationMillis,
                clientTaskId,
                taskExecutionOrderId,
                priority,
                disallowClobber,
                taskTitle,
                createdBy,
                groupingId,
            },
            storeId,
        ).then((data) => {
            AppInfoLoader.getAppConfig().then((configData) => {
                new CountMetric(storeId, 'ItemsRequested', 'IhmFlyingDutchman', configData.toolName).count(taskParams.requestedQuantity);
            });
            return data;
        });
    }

    public static cancelTask(storeId, taskId, force) {
        return IhmLighthouseServiceClient.sendRequest(
            'CancelTask',
            {
                storeId,
                taskId,
                force,
            },
            storeId,
        ).then((data) => {
            AppInfoLoader.getAppConfig().then((configData) => {
                new CountMetric(storeId, 'TasksCancelled', 'IhmFlyingDutchman', configData.toolName).count(1);
            });
            return data;
        });
    }

    public static getOptimalPath(toolName, userId, storeId) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetOptimalPath',
            {
                storeId,
                toolName,
                userId,
            },
            storeId,
        );
    }

    public static completeAndUpdateTaskPrerequisites(storeId, taskId, deviceId, userId, allowAvailableTaskToComplete) {
        return IhmLighthouseServiceClient.sendRequest(
            'CompleteAndUpdateTaskPrerequisites',
            {
                storeId,
                taskId,
                deviceId,
                userId,
                allowAvailableTaskToComplete,
            },
            storeId,
        );
    }

    public static updateTask(
        storeId,
        taskId,
        taskParams,
        taskDescription,
        requestId,
        expirationMillis,
        previousClientTaskVersion,
        taskExecutionOrderId,
        priority,
        taskTitle,
        force?,
    ) {
        return IhmLighthouseServiceClient.sendRequest(
            'UpdateTask',
            {
                storeId,
                taskId,
                taskParams,
                taskDescription,
                requestId,
                expirationMillis,
                previousClientTaskVersion,
                taskExecutionOrderId,
                priority,
                taskTitle,
                force,
            },
            storeId,
        );
    }

    public static updateTaskWithUser(storeId, taskId, taskParams, taskDescription, requestId, expirationMillis, previousClientTaskVersion, taskExecutionOrderId, updatedByUser) {
        return IhmLighthouseServiceClient.sendRequest(
            'UpdateTaskWithUser',
            {
                storeId,
                taskId,
                taskParams,
                taskDescription,
                requestId,
                expirationMillis,
                previousClientTaskVersion,
                taskExecutionOrderId,
                updatedByUser,
            },
            storeId,
        );
    }

    public static getActiveTasksByCategory(
        storeId,
        taskCategory,
        limitForAvailable = null,
        limitForInProgress = null,
        exclusiveStartKeyForAvailable = null,
        exclusiveStartKeyForInProgress = null,
        usePagination = false,
    ) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetActiveTasksByCategory',
            {
                storeId,
                taskCategory,
                limitForAvailable,
                limitForInProgress,
                exclusiveStartKeyForAvailable,
                exclusiveStartKeyForInProgress,
                usePagination,
            },
            storeId,
        );
    }

    public static startWorkOnTask(storeId, taskId, userId, taskVersion, expectNextProgressUpdateInMillis) {
        return IhmLighthouseServiceClient.sendRequest(
            'StartWorkOnTask',
            {
                storeId,
                taskId,
                userId,
                taskVersion,
                expectNextProgressUpdateInMillis,
            },
            storeId,
        );
    }

    public static getTasksByGroupingIdPrefix(storeId, taskCategory, groupingIdPrefix, taskStatus) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetTasksByGroupingIdPrefix',
            {
                storeId,
                taskCategory,
                groupingIdPrefix,
                taskStatus,
            },
            storeId,
        );
    }

    public static getInProgressTasks(storeId) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetInProgressTasks',
            {
                storeId,
            },
            storeId,
        );
    }

    public static getTask(storeId, taskId) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetTask',
            {
                storeId,
                taskId,
            },
            storeId,
        );
    }

    public static getTasksByExecutionOrderIdPrefix(storeId, taskCategory, executionOrderIdPrefix, taskStatus) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetTasksByExecutionOrderIdPrefix',
            {
                storeId,
                taskCategory,
                executionOrderIdPrefix,
                taskStatus,
            },
            storeId,
        );
    }

    public static getTasksByParentTaskId(storeId, parentTaskId, taskListGroupingId) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetTasksByParentTaskId',
            {
                storeId,
                parentTaskId,
                taskListGroupingId,
            },
            storeId,
        );
    }

    public static getWorkableTasksByCategory(storeId, taskCategory, limit, exclusiveStartKey, usePagination) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetWorkableTasksByCategory',
            {
                storeId,
                taskCategory,
                limit,
                exclusiveStartKey,
                usePagination,
            },
            storeId,
        );
    }

    public static getWorkableTaskCountsByExecutionOrderIdPrefix(storeId, taskCategory, executionOrderIdPrefixSegments) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetWorkableTaskCountsByExecutionOrderIdPrefix',
            {
                storeId,
                taskCategory,
                executionOrderIdPrefixSegments,
            },
            storeId,
        );
    }

    public static getWorkableTaskCountsPerCategory(storeId: string, taskCategory: string) {
        return IhmLighthouseServiceClient.sendRequest(
            'GetWorkableTaskCountsPerCategory',
            {
                storeId,
                taskCategory,
            },
            storeId,
        );
    }

    public static stopWorkOnTask(storeId, taskId, userId, taskComplete, reason, allowAvailableTaskToComplete) {
        return IhmLighthouseServiceClient.sendRequest(
            'StopWorkOnTask',
            {
                storeId,
                taskId,
                taskComplete,
                userId,
                reason,
                allowAvailableTaskToComplete,
            },
            storeId,
        );
    }

    public static stopWorkOnTaskBatch(storeId, stopWorkOnTaskBatchList) {
        return IhmLighthouseServiceClient.sendRequest(
            'StopWorkOnTaskBatch',
            {
                stopWorkOnTaskBatch: stopWorkOnTaskBatchList,
            },
            storeId,
        );
    }

    public static startWorkOnNextTaskByExecutionOrderId(storeId, userId, taskCategory, expectNextProgressUpdateInMillis, executionOrderIdQuery, taskIdListToSkip) {
        return IhmLighthouseServiceClient.sendRequest(
            'StartWorkOnNextTaskByExecutionOrderId',
            {
                storeId,
                taskCategory,
                userId,
                expectNextProgressUpdateInMillis,
                executionOrderIdQuery,
                taskIdListToSkip,
            },
            storeId,
        );
    }

    public static startOrResumeWorkOnNextTaskByExecutionOrderId(
        storeId,
        executionOrderIdQuery,
        expectNextProgressUpdateInMillis,
        taskCategory,
        userId = null,
        taskIdListToSkip = null,
        parentTaskId = null,
        groupingId = null,
    ) {
        return IhmLighthouseServiceClient.sendRequest(
            'StartOrResumeWorkOnNextTaskByExecutionOrderId',
            {
                storeId,
                executionOrderIdQuery,
                expectNextProgressUpdateInMillis,
                taskCategory,
                userId,
                taskIdListToSkip,
                parentTaskId,
                groupingId,
            },
            storeId,
        );
    }

    public static recordTaskProgress(storeId, taskId, userId, expectNextProgressUpdateInMillis) {
        return IhmLighthouseServiceClient.sendRequest(
            'RecordTaskProgress',
            {
                storeId,
                taskId,
                userId,
                expectNextProgressUpdateInMillis,
            },
            storeId,
        );
    }

    public static createTaskList(
        storeId,
        taskParams,
        taskCategory,
        requestId,
        priority = null,
        taskTitle = null,
        taskDescription = null,
        expirationMillis = null,
        taskExecutionOrderId = null,
        createdBy = null,
        groupingId = null,
        taskListGroupingId = null,
        parentTaskId = null,
        taskPrerequisite = null,
        clientTaskId = null,
        disallowClobber = null,
        wgmParams = null,
        taskSla = null,
    ) {
        return IhmLighthouseServiceClient.sendRequest(
            'CreateTaskList',
            {
                storeId,
                taskParams,
                taskCategory,
                requestId,
                priority,
                taskTitle,
                taskDescription,
                expirationMillis,
                taskExecutionOrderId,
                createdBy,
                groupingId,
                taskListGroupingId,
                parentTaskId,
                taskPrerequisite,
                clientTaskId,
                disallowClobber,
                wgmParams,
                taskSla,
            },
            storeId,
        );
    }
}
